import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
} from 'firebase/firestore';
import { firebaseApp } from './config';
import { MaintenanceLogType } from '../interfaces/MaintenanceLogType';
import { getCurrentUserId } from './auth-helper';
import { createSettingsItem, loadSettings } from './db-settings';

const db = getFirestore(firebaseApp);

/**
 * Erstellt ein MaintenanceLogType-Objekt aus Firestore-Daten.
 */
function createMaintenanceItem(data: any): MaintenanceLogType {
  return {
    logId: data.logId,
    createdBy: data.createdBy || '',
    date: data.date ? new Date(data.date.toDate()) : undefined,
    comment: data.comment || '',
    droneType: data.droneType || '',
    maintenanceType: data.maintenanceType || '',
    errors: data.errors || '',
  };
}

/**
 * Speichert ein Wartungs-Log in Firestore.
 */
export async function saveMaintenanceLog(log: MaintenanceLogType) {
  console.log('Speichere Wartungs-Log');
  const uid = getCurrentUserId();
  if (!uid) {
    return;
  }

  const settings = await loadSettings();
  let zugriffscode: string | undefined = undefined;
  if (settings == undefined) {
    return;
  } else {
    const zugangscode = settings.zugangscode;
    if (settings.zugangscode != createSettingsItem({}).zugangscode) {
      console.log('Zugriffscode gefunden');
      zugriffscode = zugangscode;
    }
  }
  try {
    const ref = doc(
      db,
      'users',
      zugriffscode ? zugriffscode : uid,
      'maintenanceLogs',
      log.logId
    );
    await setDoc(ref, log);
    console.log('Wartungs-Log gespeichert:', log);
  } catch (error) {
    console.error('Fehler beim Speichern des Wartungs-Logs:', error);
  }
}

export async function saveMaintenanceLogs(logs: MaintenanceLogType[]) {
  console.log('saveLogs');
  const uid = getCurrentUserId();
  if (uid == undefined) {
    return;
  }
  const settings = await loadSettings();
  let zugriffscode: string | undefined = undefined;
  if (settings == undefined) {
    return [];
  } else {
    const zugangscode = settings.zugangscode;
    if (settings.zugangscode != createSettingsItem({}).zugangscode) {
      console.log('Zugriffscode gefunden');
      zugriffscode = zugangscode;
    }
  }
  logs.forEach((item) => {
    const ref = doc(
      db,
      'users',
      zugriffscode ? zugriffscode : uid,
      'maintenanceLogs',
      item.logId
    );
    setDoc(ref, item);
  });
}

/**
 * Lädt ein spezifisches Wartungs-Log aus Firestore.
 */
export async function loadMaintenanceLog(
  logId: string
): Promise<MaintenanceLogType | undefined> {
  console.log('Lade Wartungs-Log:', logId);
  const uid = getCurrentUserId();
  if (!uid) {
    return undefined;
  }

  const settings = await loadSettings();
  let zugriffscode: string | undefined = undefined;
  if (settings == undefined) {
    return undefined;
  } else {
    const zugangscode = settings.zugangscode;
    if (settings.zugangscode != createSettingsItem({}).zugangscode) {
      console.log('Zugriffscode gefunden');
      zugriffscode = zugangscode;
    }
  }

  const q = query(
    collection(
      db,
      'users',
      zugriffscode ? zugriffscode : uid,
      'maintenanceLogs'
    ),
    where('logId', '==', logId)
  );
  const querySnapshot = await getDocs(q);

  let result: MaintenanceLogType | undefined = undefined;
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    console.log('Gefundenes Wartungs-Log:', logId);
    result = createMaintenanceItem(data);
  });

  return result;
}

/**
 * Lädt alle Wartungs-Logs für den aktuellen Benutzer.
 */
export async function loadMaintenanceLogs(
  pageSize: number = 10,
  lastLog?: MaintenanceLogType,
  filters: { field: string; value: any }[] = []
): Promise<MaintenanceLogType[]> {
  console.log('Lade Wartungs-Logs');
  const uid = getCurrentUserId();
  if (!uid) {
    return [];
  }

  const settings = await loadSettings();
  let zugriffscode: string | undefined = undefined;
  if (settings == undefined) {
    return [];
  } else {
    const zugangscode = settings.zugangscode;
    if (settings.zugangscode != createSettingsItem({}).zugangscode) {
      console.log('Zugriffscode gefunden');
      zugriffscode = zugangscode;
    }
  }

  try {
    let q = query(
      collection(
        db,
        'users',
        zugriffscode ? zugriffscode : uid,
        'maintenanceLogs'
      ),
      orderBy('date', 'desc')
    );

    filters.forEach(({ field, value }) => {
      q = query(q, where(field, '==', value));
    });

    if (lastLog) {
      q = query(q, startAfter(lastLog.date));
    }

    q = query(q, limit(pageSize));

    const querySnapshot = await getDocs(q);
    const logs: MaintenanceLogType[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      logs.push(createMaintenanceItem(data));
    });

    console.log(`Geladene Wartungs-Logs: ${logs.length}`);
    return logs;
  } catch (error) {
    console.error('Fehler beim Laden der Wartungs-Logs:', error);
    return [];
  }
}

/**
 * Löscht ein Wartungs-Log basierend auf der logId.
 */
export async function deleteMaintenanceLog(logId: string): Promise<boolean> {
  console.log('Lösche Wartungs-Log:', logId);
  const uid = getCurrentUserId();
  if (!uid) {
    return false;
  }

  try {
    const ref = doc(db, 'users', uid, 'maintenanceLogs', logId);
    await deleteDoc(ref);
    console.log('Wartungs-Log gelöscht:', logId);
    return true;
  } catch (error) {
    console.error('Fehler beim Löschen des Wartungs-Logs:', error);
    return false;
  }
}

/**
 * Gibt die Anzahl der Wartungs-Logs zurück.
 */
export async function getMaintenanceLogCount(
  filters: { field: string; value: any }[] = []
) {
  console.log('Zähle Wartungs-Logs');
  const uid = getCurrentUserId();
  if (!uid) {
    return 0;
  }

  const settings = await loadSettings();
  let zugriffscode: string | undefined = undefined;
  if (settings == undefined) {
    return 0;
  } else {
    const zugangscode = settings.zugangscode;
    if (settings.zugangscode != createSettingsItem({}).zugangscode) {
      console.log('Zugriffscode gefunden');
      zugriffscode = zugangscode;
    }
  }

  try {
    let q = query(
      collection(
        db,
        'users',
        zugriffscode ? zugriffscode : uid,
        'maintenanceLogs'
      )
    );

    filters.forEach(({ field, value }) => {
      q = query(q, where(field, '==', value));
    });

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error('Fehler beim Zählen der Wartungs-Logs:', error);
    return 0;
  }
}
